import type { SemanticColourTheme } from './types';

/**
 * The `color` vocabulary for text-bearing components (`Text`, `Icon`,
 * `Heading`, `TextLink`).
 *
 * Keys name the semantic token they resolve to, so a call site reads as the
 * token it gets: `<Text color="textBody">` is `semantic.colour.text.textBody`.
 * The four status keys are the leaves of `semantic.colour.text.reserved.*`,
 * which is why they are unprefixed.
 *
 * Resolved through these maps rather than a bare `semantic.colour.text[key]`
 * index because `reserved` is nested a level deeper than the flat leaves, and
 * because `accent` has no semantic token at all yet.
 */

/**
 * No semantic token carries this value; the nearest is 67 (RGB euclidean)
 * away, which is a different colour rather than a rounding difference. Held
 * here so the legacy `palette` layer can be deleted, and so the one remaining
 * consumer is visible to whoever adds the real token. RWR-18028.
 */
const ACCENT_WITHOUT_TOKEN = '#3C3391';

const statusColors = (colour: SemanticColourTheme) => ({
  critical: colour.text.reserved.critical,
  warning: colour.text.reserved.warning,
  info: colour.text.reserved.info,
  success: colour.text.reserved.success,
  accent: ACCENT_WITHOUT_TOKEN,
});

export const getTextColors = (colour: SemanticColourTheme) => ({
  textPrimary: colour.text.textPrimary,
  textBody: colour.text.textBody,
  textSecondary: colour.text.textSecondary,
  textTertiary: colour.text.textTertiary,
  textDisabled: colour.text.textDisabled,
  textLink: colour.text.textLink,
  ...statusColors(colour),
});

export const getHeadingColors = (colour: SemanticColourTheme) => ({
  textHeadings: colour.text.textHeadings,
  textBody: colour.text.textBody,
  textSecondary: colour.text.textSecondary,
  textTertiary: colour.text.textTertiary,
  textLink: colour.text.textLink,
  ...statusColors(colour),
});

export type TextColorKey = keyof ReturnType<typeof getTextColors>;
export type HeadingColorKey = keyof ReturnType<typeof getHeadingColors>;
